import { LineItem } from "../DataTypes";

export default class PricingCalculator {
	total: number;
	formatter: Intl.NumberFormat;
	lineItems: LineItem[];
	taxRate: number;

	constructor() {
		this.total = 0;
		this.formatter = new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: "USD",
			minimumFractionDigits: 2,
		});
		this.lineItems = [] as LineItem[];
		this.taxRate = 0;
	}

	round(price: number) {
		return Math.round(price * 100) / 100;
	}

	addLineItem(lineItem: LineItem): PricingCalculator {
		this.lineItems.push(lineItem);
		return this;
	}

	removeLineItem(lineItem: LineItem): PricingCalculator {
		this.lineItems = this.lineItems.filter((item) => item !== lineItem);
		return this;
	}

	getLineItems(): LineItem[] {
		return this.lineItems;
	}

	getLineItemTotal(lineItem: LineItem, includeTax: boolean = false): number {
		return this.round(lineItem.price * (includeTax ? this.getTaxRate() / 100 : 1));
	}

	setTaxRate(taxRate: number): PricingCalculator {
		this.taxRate = taxRate;
		return this;
	}

	getTaxRate(): number {
		return this.taxRate;
	}

	/**
	 * This rounds each line item and adds it to the total.
	 * This prevents the lineItems from mismatching the total
	 * by a few cents.
	 *
	 * @returns {PricingCalculator} total
	 */
	calculateTotal(includeTax: boolean = false): PricingCalculator {
		let total: number = 0;

		this.lineItems.forEach((lineItem) => (total += this.round(lineItem.price)));

		this.total = this.round(total * (includeTax ? this.getTaxRate() / 100 : 1));

		return this;
	}

	getTotal(): number {
		return this.total;
	}

	getFormattedTotal(): string {
		return this.formatter.format(this.total);
	}
}

const formatter = new Intl.NumberFormat("en-US", {
	style: "currency",
	currency: "USD",
});

/**
 * The quantity that per-unit upgrade charges apply to. Upgrades are charged on the package
 * quantity (baseQuantity), capped at the line quantity. Falls back to the line quantity when
 * baseQuantity is unset (root/standalone items), and the min() handles downward overrides
 * (e.g. only 4 of 12 decorated) so we never upgrade items that do not exist.
 */
export function getUpgradeQuantity(item: LineItem): number {
	const quantity = item.quantity || 0;
	const baseQuantity = item.baseQuantity || 0;
	return baseQuantity > 0 ? Math.min(quantity, baseQuantity) : quantity;
}

export function getItemPrice(item: LineItem): [number, string] {
	let value = 0;
	if (item.systemCategoryId === 1) value = item.price * (item.quantity || 0);
	else {
		// When price is included (struck through), only charge for extra/override items
		if (item.includePrice) {
			// Only charge for the override quantity
			if (item.isManuallyOverridden && item.quantityOverride > 0) {
				value = item.price * (item.quantityOverride || 0);
			}
		} else {
			// Normal pricing - charge for full quantity
			value = item.price * (item.quantity || 0);
		}

		// Upgrades apply to the package quantity only, never the extras keyed above it.
		// Extras (quantity - baseQuantity) are already charged at the full item rate above.
		const upgradeQuantity = getUpgradeQuantity(item);
		if (item.upCharge) {
			value += item.upCharge * upgradeQuantity;
		}
		if (item.colorUpCharges) {
			value += item.colorUpCharges * upgradeQuantity;
		}
	}

	if (item.sizeUpCharge) {
		const totalSpecialSizeQuantity =
			item.specialSizeSelections?.reduce((total, selection) => total + selection.quantity, 0) || 0;
		value += item.sizeUpCharge * totalSpecialSizeQuantity;
	}

	return [value, formatter.format(parseFloat(value.toFixed(2)))];
}

// Recursive total value of children lineItem
export function getTotalPrice(items: LineItem[], total: number = 0) {
	let totalValue = total;
	items.forEach((item) => {
		totalValue += getItemPrice(item)[0];
		if (item.children) totalValue += getTotalPrice(item.children);
	});
	return totalValue;
}

export function renderPrice(value: number) {
	return isNaN(value) ? "$0.00" : formatter.format(value);
}
